import { useState, useRef, useEffect } from "react";
import { useNavigate, Navigate } from "react-router";
import { useAdminAuth } from "./AdminAuthContext";
import { Eye, EyeOff } from "lucide-react";
import defaultLogoSvg from "../../../imports/MoechaLogo.svg";

export function AdminLoginPage() {
  const { login, isAuthenticated } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => { return () => { mountedRef.current = false; }; }, []);

  if (isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    await new Promise((r) => setTimeout(r, 400));
    if (!mountedRef.current) return;
    const result = await login(email, password);
    if (!mountedRef.current) return;
    setLoading(false);
    if (result.success) {
      navigate("/admin");
    } else {
      setError(result.error || "Login failed");
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: "#F7F5F1", fontFamily: "'Jost', sans-serif" }}
    >
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <img
              src={defaultLogoSvg}
              alt="Moecha Logo"
              className="max-w-full max-h-full object-contain"
            />
          </div>
          <p
            className="text-[#6F675F] tracking-[0.25em] uppercase"
            style={{ fontSize: "10px" }}
          >
            Studio Dashboard
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label
              className="block text-[#6F675F] tracking-[0.15em] uppercase mb-2"
              style={{ fontSize: "11px", fontWeight: 400 }}
            >
              Email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3.5 bg-white border border-[#E6E0D7] rounded-lg text-[#2D2A26] placeholder:text-[#D8D1C7] focus:outline-none focus:border-[#C7A76C] transition-colors"
              style={{ fontSize: "14px", fontWeight: 300 }}
              placeholder="your@email.com"
            />
          </div>

          <div>
            <label
              className="block text-[#6F675F] tracking-[0.15em] uppercase mb-2"
              style={{ fontSize: "11px", fontWeight: 400 }}
            >
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3.5 bg-white border border-[#E6E0D7] rounded-lg text-[#2D2A26] placeholder:text-[#D8D1C7] focus:outline-none focus:border-[#C7A76C] transition-colors pr-12"
                style={{ fontSize: "14px", fontWeight: 300 }}
                placeholder="Enter password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#D8D1C7] hover:text-[#6F675F] transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {error && (
            <p className="text-[#8B5C58]" style={{ fontSize: "13px" }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 bg-[#6F7F63] text-white tracking-[0.2em] uppercase rounded-lg hover:bg-[#5F6F53] transition-all duration-300 disabled:opacity-50"
            style={{ fontSize: "12px", fontWeight: 400 }}
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}